import { type ReactNode } from 'react';

import { Box, type BoxProps } from '@mui/material';

export type ScreenRootProps = {
  /** Adds `position: relative`, for screens that pin a footer or sheet over
   *  themselves. Roughly a third of them do. */
  relative?: boolean;
  children?: ReactNode;
  sx?: BoxProps['sx'];
};

/**
 * The frame every screen sits in: fills its parent, stacks its children in a
 * column, paints the page background and clips its own overflow so anything
 * inside that scrolls does so against a bounded box.
 *
 * Those five declarations were written out at 38 call sites — `AppScreen` and
 * `StackPage` among them, so the two scaffolds that exist to stop screens
 * repeating themselves were repeating this. It is a template rather than a
 * variant because it carries layout and paint together: a Card variant can
 * describe a surface, but nothing in the theme can say "fill the parent and
 * clip".
 *
 * It deliberately does NOT own padding, the status bar, the app bar or a
 * footer. Those differ per screen and belong to AppScreen / StackPage, which
 * compose this.
 */
const ScreenRoot = ({ relative = false, children, sx }: ScreenRootProps) => (
  <Box
    sx={[
      {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'hidden',
      },
      relative ? { position: 'relative' } : {},
      ...(Array.isArray(sx) ? sx : [sx ?? {}]),
    ]}
  >
    {children}
  </Box>
);

ScreenRoot.displayName = 'ScreenRoot';
export default ScreenRoot;
